import Cookies from 'js-cookie'

import { CookieKeys } from '@/configs/cookies'
import { decodeJwtPayload, isJwtPayloadValid } from '@/lib/auth/jwt'

import { resetSession } from './session'
import { uuid } from './uuid'

const DISTINCT_KEY = 'arena_pa_distinct_id'
const USER_KEY = 'arena_pa_user_id'

let memoryDistinct: string | null = null
let currentUserId: string | null = null

function storageGet(): string | null {
  try {
    return window.localStorage.getItem(DISTINCT_KEY)
  } catch {
    return null
  }
}

function storageSet(value: string): boolean {
  try {
    window.localStorage.setItem(DISTINCT_KEY, value)
    return true
  } catch {
    return false
  }
}

function storedUserGet(): string | null {
  try {
    return window.localStorage.getItem(USER_KEY)
  } catch {
    return null
  }
}

function storedUserSet(value: string | null): void {
  try {
    if (value) window.localStorage.setItem(USER_KEY, value)
    else window.localStorage.removeItem(USER_KEY)
  } catch {
    // storage unavailable
  }
}

export function getDistinctId(): string {
  const stored = storageGet()
  if (stored) return stored
  if (memoryDistinct) return memoryDistinct
  const id = uuid()
  memoryDistinct = id
  storageSet(id)
  return id
}

// A page can track on a hard load before AnalyticsIdentity syncs the store.
function userIdFromAccessToken(): string | null {
  try {
    const payload = decodeJwtPayload(Cookies.get(CookieKeys.ACCESS_TOKEN))
    if (!isJwtPayloadValid(payload)) return null
    return typeof payload.user_id === 'string' && payload.user_id
      ? payload.user_id
      : null
  } catch {
    return null
  }
}

export function getUserId(): string | null {
  return currentUserId ?? userIdFromAccessToken()
}

// Logout or account switch must not merge two accounts' activity (Frontend §2.2).
// The last user id is persisted so a switch is detected across reloads too.
export function setUserId(id: string | null): void {
  const previous = currentUserId
  currentUserId = id
  if (id === null) {
    if (previous) {
      resetIdentity()
      storedUserSet(null)
    }
    return
  }
  const last = previous ?? storedUserGet()
  if (last && last !== id) {
    resetIdentity()
  }
  storedUserSet(id)
}

export function resetIdentity(): void {
  memoryDistinct = null
  try {
    window.localStorage.removeItem(DISTINCT_KEY)
  } catch {
    // nothing stored
  }
  resetSession()
}

export function resetIdentityForTests(): void {
  currentUserId = null
  storedUserSet(null)
  resetIdentity()
}

export function resetUserIdMemoryForTests(): void {
  currentUserId = null
}
